<template>
  <div>
    <div class="mc-modal__filter-item">
      <FormulateInput
        :data-me="`${dataMePrefix}-input-vehicles`"
        name="vehicles"
        type="value-help-v2"
        :label="labels.licensePlate"
        :placeholder="placeholders.licensePlate"
        :modalTitle="modalTitles.vehicle"
        :value="formValues.vehicles"
        :fullObject="fullObjects.vehicles"
        :listGetter="resourcefulQuery.vehicles"
        :tableFields="vehicleFields"
        :headerFilterTopComponent="vehicleHeaderFilters"
        trackBy="id"
        displayedBy="licensePlate"
        multiple
        canSelectAll
        @update:formValues="updateFormValues({ key: 'vehicles', value: $event })"
        @update:fullObject="updateFullObjects({ key: 'vehicles', value: $event })"
      />
    </div>

    <div class="mc-modal__filter-item">
      <FormulateInput
        :data-me="`${dataMePrefix}-input-drivers`"
        name="drivers"
        type="value-help-v2"
        :label="labels.driver"
        :placeholder="placeholders.driver"
        :modalTitle="modalTitles.driver"
        :value="formValues.drivers"
        :fullObject="fullObjects.drivers"
        :listGetter="resourcefulQuery.drivers"
        :tableFields="driverFields"
        trackBy="userId"
        displayedBy="fullname"
        multiple
        canSelectAll
        @update:formValues="updateFormValues({ key: 'drivers', value: $event })"
        @update:fullObject="updateFullObjects({ key: 'drivers', value: $event })"
      />
    </div>

    <div class="mc-modal__filter-item">
      <FormulateInput
        :data-me="`${dataMePrefix}-input-submitted-period`"
        name="submitted_period"
        type="date-picker"
        datepickerType="datetime"
        :label="labels.submittedAt"
        :placeholder="placeholders.submittedAt"
        :value="formValues.submitted_period"
        valueType="date"
        format="DD MMM YYYY, HH:mm"
        titleFormat="DD MMM YYYY, HH:mm"
        rangeSeparator=" - "
        range
        draft
        confirm
        :confirm-text="buttonLabels.apply"
        :lang="datePickerLang"
        @input="updateFormValues({ key: 'submitted_period', value: $event })"
      />
    </div>

    <div class="mc-modal__filter-item">
      <div class="formulate-input">
        <label class="formulate-input-label">{{ labels.result }}</label>
        <div class="mc-modal__filter-button-group">
          <button
            v-for="option in resultOptions"
            :key="option.key"
            :data-me="`${dataMePrefix}-btn-result-${option.key}`"
            type="button"
            class="mc-modal__filter-button"
            :class="{ 'mc-modal__filter-button--active': isResultActive(option.key) }"
            @click="handleResultChange(option.key)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import datePickerLang from '@/helpers/date-picker-lang'
import {
  GET_VEHICLE_FILTER,
  GET_VEHICLE_GROUPS,
  GET_VEHICLE_TYPES,
} from '@/services/api/master/fleet'
import { GET_EMPLOYEES_PAGINATED } from '@/services/api/master/employees'

export default {
  props: {
    formValues: {
      type: Object,
      required: true,
    },
    fullObjects: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dataMePrefix: 'vehicle-inspection-filter',
      resultCheck: {
        all: true,
        eligible: false,
        notEligible: false,
      },
    }
  },
  computed: {
    labels() {
      return {
        licensePlate: this.$t('vehicleInspection.field.licensePlate'),
        driver: this.$t('vehicleInspection.field.driver'),
        submittedAt: this.$t('vehicleInspection.field.submittedAt'),
        result: this.$t('vehicleInspection.field.result'),
      }
    },
    placeholders() {
      return {
        licensePlate: this.$t('vehicleInspection.filter.selectVehicle'),
        driver: this.$t('vehicleInspection.filter.selectDriver'),
        submittedAt: this.$t('vehicleInspection.filter.selectPeriod'),
      }
    },
    modalTitles() {
      return {
        vehicle: this.$t('vehicleInspection.filter.vehicleModalTitle'),
        driver: this.$t('vehicleInspection.filter.driverModalTitle'),
      }
    },
    buttonLabels() {
      return { apply: this.$t('vehicleInspection.filter.apply') }
    },
    resultOptions() {
      return [
        { key: 'all', value: null, label: this.$t('vehicleInspection.status.all') },
        { key: 'eligible', value: true, label: this.$t('vehicleInspection.status.eligible') },
        {
          key: 'notEligible',
          value: false,
          label: this.$t('vehicleInspection.status.notEligible'),
        },
      ]
    },
    vehicleFields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('vehicleInspection.field.licensePlate'),
          isSortable: true,
        },
        {
          key: 'vehicleTypeLabel',
          label: this.$t('vehicleInspection.field.vehicleType'),
          isSortable: false,
        },
        {
          key: 'vehicleGroupLabel',
          label: this.$t('vehicleInspection.field.vehicleGroup'),
          isSortable: false,
        },
      ]
    },
    vehicleHeaderFilters() {
      return [
        {
          name: 'vehicleTypeId',
          type: 'select-resourceful-v2',
          placeholder: this.$t('vehicleInspection.field.vehicleType'),
          trackBy: 'id',
          optionsGetter: this.getVehicleTypeOptions,
          optionLabelFn: ({ item }) => item.type,
        },
        {
          name: 'vgroupId',
          type: 'select-resourceful-v2',
          placeholder: this.$t('vehicleInspection.field.vehicleGroup'),
          trackBy: 'id',
          optionsGetter: this.getVehicleGroupOptions,
          optionLabelFn: ({ item }) => item.name,
        },
      ]
    },
    driverFields() {
      return [
        {
          key: 'fullname',
          label: this.$t('vehicleInspection.field.driver'),
          isSortable: true,
        },
      ]
    },
    datePickerLang() {
      return datePickerLang()
    },
    resourcefulQuery() {
      return {
        vehicles: this.getVehicleOptions,
        drivers: this.getInspectorOptions,
      }
    },
  },
  watch: {
    'formValues.result': {
      immediate: true,
      handler(result) {
        this.setResultCheck(result)
      },
    },
  },
  methods: {
    async getVehicleOptions(options = {}) {
      const headerFilters = this.getHeaderFilterValues(options.filters)
      const response = await this.$apollo.query({
        query: GET_VEHICLE_FILTER,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            status: ['I', 'R', 'A'],
            parentOnly: true,
            pagination: { page: options.page || 1, show: options.limit || 10 },
            search: options.search || undefined,
            orderBy: options.sorts,
            vehicleTypeId: headerFilters.vehicleTypeId,
            vgroupId: headerFilters.vgroupId,
          },
        },
      })
      const result = response.data.vehiclesFilter.vehiclesFilterPaginated
      const data = result.data.map((vehicle) => ({
        ...vehicle,
        vehicleTypeLabel: vehicle.vehicleType?.type ?? '-',
        vehicleGroupLabel:
          vehicle.vehicle2Vgroup?.map(({ vgroup }) => vgroup.name).join(', ') || '-',
      }))
      return {
        metadata: { total_count: result.pagination.total, page: result.pagination.page },
        data: {
          paginated_result: data,
          ids: result.ids,
        },
      }
    },
    async getVehicleTypeOptions(options = {}) {
      const response = await this.$apollo.query({
        query: GET_VEHICLE_TYPES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            hasVehicle: true,
            isActive: true,
            pagination: { page: options.page || 1, show: options.limit || 10 },
            search: options.search || undefined,
          },
        },
      })
      const result = response.data.vehicleTypes.vehicleTypes
      return {
        metadata: { total_count: result.pagination.total, page: result.pagination.page },
        data: {
          paginated_result: result.data.map((vehicleType) => ({
            ...vehicleType,
            id: vehicleType.vehicleTypeId,
          })),
        },
      }
    },
    async getVehicleGroupOptions(options = {}) {
      const response = await this.$apollo.query({
        query: GET_VEHICLE_GROUPS,
        fetchPolicy: 'no-cache',
        variables: { filter: {} },
      })
      const search = options.search?.trim().toLowerCase()
      const vehicleGroups = response.data.vgroups.vgroups.filter(
        (vehicleGroup) => !search || vehicleGroup.name.toLowerCase().includes(search),
      )
      return {
        metadata: { total_count: vehicleGroups.length, page: 1 },
        data: { paginated_result: vehicleGroups },
        isPagination: false,
      }
    },
    getHeaderFilterValues(filters = []) {
      return filters.reduce((result, filter) => {
        const match = filter.match(/^(vehicleTypeId|vgroupId) eq '([^']+)'$/)
        if (match) result[match[1]] = Number(match[2])
        return result
      }, {})
    },
    async getInspectorOptions(options = {}) {
      const response = await this.$apollo.query({
        query: GET_EMPLOYEES_PAGINATED,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            pagination: { page: options.page || 1, show: options.limit || 10 },
            search: options.search || undefined,
            orderBy: options.sorts,
          },
        },
      })
      const result = response.data.employeesPaginated.employeesPaginated
      return {
        metadata: { total_count: result.pagination.total, page: result.pagination.page },
        data: { paginated_result: result.data },
      }
    },
    updateFormValues(payload) {
      this.$emit('update:formValues', payload)
    },
    updateFullObjects(payload) {
      this.$emit('update:fullObjects', payload)
    },
    isResultActive(resultKey) {
      return this.resultCheck[resultKey]
    },
    handleResultChange(resultKey) {
      const selectedOption = this.resultOptions.find((option) => option.key === resultKey)
      this.updateResultCheck(resultKey)
      this.updateFormValues({
        key: 'result',
        value: selectedOption.value,
      })
    },
    setResultCheck(result) {
      const resultKey =
        result === true ? 'eligible' : result === false ? 'notEligible' : 'all'
      this.updateResultCheck(resultKey)
    },
    updateResultCheck(activeKey) {
      Object.keys(this.resultCheck).forEach((key) => {
        this.resultCheck[key] = key === activeKey
      })
    },
  },
}
</script>
